import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { FaEllipsis } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { isVideoShowingAtom } from "../../VideoRooms/VideoParts/videoAtoms";
import { allocateNavItems, sumBadgeNumbers } from "./allocateNavItems";
import MoreMenu from "./MoreMenu";
import NavBarButton from "./NavBarButton";
import { NavItem } from "./types";

type MobileNavbarViewProps = {
    items: NavItem[];
    maxBarSlots: number;
};

export default function MobileNavbarView({
    items,
    maxBarSlots,
}: MobileNavbarViewProps) {
    const isVideoShowing = useAtomValue(isVideoShowingAtom);

    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const [pendingRoute, setPendingRoute] = useState<string | null>(null);

    const { barItems, menuItems } = allocateNavItems(items, maxBarSlots);

    const isItemSelected = (item: NavItem): boolean => {
        if (pendingRoute !== null && item.routeName) {
            return pendingRoute === item.routeName;
        }
        return !!item.selected;
    };

    const activateItem = (item: NavItem): void => {
        if (item.routeName) {
            setPendingRoute(item.routeName);
            setIsMoreMenuOpen(false);
        }
        item.onClick?.();
    };

    useEffect(() => {
        // A page restored from the back/forward cache still shows the tab tapped before leaving it.
        const clearPendingRoute = () => setPendingRoute(null);
        window.addEventListener("pageshow", clearPendingRoute);
        return () => window.removeEventListener("pageshow", clearPendingRoute);
    }, []);

    return isVideoShowing ? null : (
        <div
            id="mobile-navbar"
            className="sticky bottom-0 z-10 flex min-h-16 items-stretch justify-between self-stretch border-t border-brand-border bg-white print:hidden"
        >
            {barItems.map((item) => (
                <NavBarButton
                    key={item.name}
                    icon={item.icon}
                    name={item.name}
                    selected={isItemSelected(item)}
                    badgeNumbers={item.badgeNumbers}
                    onActivate={() => activateItem(item)}
                    tooltip={item.tooltip}
                />
            ))}
            <NavBarButton
                icon={isMoreMenuOpen ? IoClose : FaEllipsis}
                name={isMoreMenuOpen ? "Close" : "More"}
                selected={menuItems.some(isItemSelected)}
                badgeNumbers={sumBadgeNumbers(menuItems)}
                onActivate={(e) => {
                    // Stop propagation so MoreMenu's outside-click handler doesn't close it immediately
                    e.stopPropagation();
                    setIsMoreMenuOpen((prev) => !prev);
                }}
            />
            {isMoreMenuOpen && (
                <MoreMenu
                    items={menuItems}
                    isItemSelected={isItemSelected}
                    onItemActivated={activateItem}
                    onDismiss={() => setIsMoreMenuOpen(false)}
                />
            )}
        </div>
    );
}
